import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { AdminService } from '../../services/admin.service';
import { FormsModule } from '@angular/forms';

export interface Relative {
  id: number;
  firstName: string;
  middleName?: string;
  lastName: string;
  email?: string;
  mobileNumber?: string;
  referralCode: string;
}

export interface RelationshipPathSegment {
  relativeId: number;
  relativeName: string;
  relationshipType: string; // "Parent", "Child", "Sibling", "Spouse", "Self"
  distance: number; // Steps from person1
  profileImageUrl?: string; // Profile image URL for the relative
}

export interface RelationshipResult {
  person1Id: number;
  person1Name: string;
  person1ProfileImageUrl?: string;
  person2Id: number;
  person2Name: string;
  person2ProfileImageUrl?: string;
  relationship: string;
  detailedRelationship?: string;
  relationshipPath: string[]; // Legacy format for backward compatibility
  detailedPath?: RelationshipPathSegment[]; // Enhanced path with segment details
  pathMethod?: string; // "Direct", "CommonAncestor", "BFS", "InLaw"
  calculationTimeMs?: number; // Time taken to calculate in milliseconds
}

@Component({
  selector: 'app-relationship-calculator',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './relationship-calculator.component.html',
  styleUrl: './relationship-calculator.component.css'
})
export class RelationshipCalculatorComponent implements OnInit, OnDestroy {
  person1SearchTerm = '';
  person2SearchTerm = '';
  person1Results: Relative[] = [];
  person2Results: Relative[] = [];
  selectedPerson1: Relative | null = null;
  selectedPerson2: Relative | null = null;
  showPerson1Dropdown = false;
  showPerson2Dropdown = false;
  isLoadingPerson1 = false;
  isLoadingPerson2 = false;
  relationshipResult: RelationshipResult | null = null;
  isLoadingRelationship = false;
  errorMessage = '';
  isPerson1Disabled = false; // For non-admin users, Person1 is disabled
  
  // Progress tracking
  progressPhase: string = '';
  progressPercentage: number = 0;
  elapsedTime: number = 0;
  progressMessage: string = '';
  private progressTimer: any = null;
  private elapsedTimer: any = null;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private adminService: AdminService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    // Check if user is admin - if not, check for pre-selected Person1
    if (!this.authService.isAdmin()) {
      this.route.queryParams.subscribe(params => {
        const person1Id = params['person1Id'];
        const disabled = params['disabled'] === 'true';
        
        if (person1Id && disabled) {
          this.isPerson1Disabled = true;
          this.loadPerson1ById(parseInt(person1Id, 10));
        }
      });
    }
  }

  loadPerson1ById(person1Id: number): void {
    this.isLoadingPerson1 = true;
    this.adminService.getRelativeById(person1Id).subscribe({
      next: (response: any) => {
        this.isLoadingPerson1 = false;
        if (response.success && response.data) {
          const relative = response.data;
          this.selectedPerson1 = {
            id: relative.id,
            firstName: relative.firstName,
            middleName: relative.middleName,
            lastName: relative.lastName,
            email: relative.email,
            mobileNumber: relative.mobileNumber,
            referralCode: relative.referralCode
          };
          this.person1SearchTerm = `${relative.firstName} ${relative.middleName || ''} ${relative.lastName}`.trim();
        }
      },
      error: (error) => {
        this.isLoadingPerson1 = false;
        console.error('Error loading Person1:', error);
      }
    });
  }

  searchPerson1(): void {
    // Don't allow searching if Person1 is disabled (for non-admin users)
    if (this.isPerson1Disabled) {
      return;
    }

    if (this.person1SearchTerm.length < 2) {
      this.person1Results = [];
      this.showPerson1Dropdown = false;
      return;
    }

    this.isLoadingPerson1 = true;
    this.showPerson1Dropdown = true;

    this.authService.getPublicRelatives(this.person1SearchTerm).subscribe({
      next: (response: any) => {
        this.isLoadingPerson1 = false;
        let relatives = [];
        if (response?.data?.relatives) {
          relatives = response.data.relatives;
        } else if (response?.relatives) {
          relatives = response.relatives;
        } else if (Array.isArray(response?.data)) {
          relatives = response.data;
        } else if (Array.isArray(response)) {
          relatives = response;
        }
        this.person1Results = relatives;
      },
      error: (error) => {
        this.isLoadingPerson1 = false;
        console.error('Error searching person 1:', error);
        this.person1Results = [];
      }
    });
  }

  searchPerson2(): void {
    if (this.person2SearchTerm.length < 2) {
      this.person2Results = [];
      this.showPerson2Dropdown = false;
      return;
    }

    this.isLoadingPerson2 = true;
    this.showPerson2Dropdown = true;

    this.authService.getPublicRelatives(this.person2SearchTerm).subscribe({
      next: (response: any) => {
        this.isLoadingPerson2 = false;
        let relatives = [];
        if (response?.data?.relatives) {
          relatives = response.data.relatives;
        } else if (response?.relatives) {
          relatives = response.relatives;
        } else if (Array.isArray(response?.data)) {
          relatives = response.data;
        } else if (Array.isArray(response)) {
          relatives = response;
        }
        this.person2Results = relatives;
      },
      error: (error) => {
        this.isLoadingPerson2 = false;
        console.error('Error searching person 2:', error);
        this.person2Results = [];
      }
    });
  }

  selectPerson1(relative: Relative): void {
    this.selectedPerson1 = relative;
    this.person1SearchTerm = `${relative.firstName} ${relative.middleName || ''} ${relative.lastName}`.trim();
    this.showPerson1Dropdown = false;
    this.person1Results = [];
    this.calculateRelationship();
  }

  selectPerson2(relative: Relative): void {
    this.selectedPerson2 = relative;
    this.person2SearchTerm = `${relative.firstName} ${relative.middleName || ''} ${relative.lastName}`.trim();
    this.showPerson2Dropdown = false;
    this.person2Results = [];
    this.calculateRelationship();
  }

  clearPerson1(): void {
    // Don't allow clearing if Person1 is disabled (for non-admin users)
    if (this.isPerson1Disabled) {
      return;
    }
    this.selectedPerson1 = null;
    this.person1SearchTerm = '';
    this.relationshipResult = null;
  }

  clearPerson2(): void {
    this.selectedPerson2 = null;
    this.person2SearchTerm = '';
    this.relationshipResult = null;
  }

  calculateRelationship(): void {
    if (!this.selectedPerson1 || !this.selectedPerson2) {
      return;
    }

    if (this.selectedPerson1.id === this.selectedPerson2.id) {
      this.errorMessage = 'Please select two different people';
      this.relationshipResult = null;
      return;
    }

    this.isLoadingRelationship = true;
    this.errorMessage = '';
    this.relationshipResult = null;
    
    // Reset progress
    this.progressPhase = 'Direct';
    this.progressPercentage = 0;
    this.elapsedTime = 0;
    this.progressMessage = 'Checking direct relationships...';
    
    // Start elapsed time tracker
    const startTime = Date.now();
    this.elapsedTimer = setInterval(() => {
      this.elapsedTime = Math.floor((Date.now() - startTime) / 1000);
      this.updateProgressBasedOnTime();
    }, 100);
    
    // Start progress simulator
    this.startProgressSimulation();

    this.http.post<{ success: boolean; data: RelationshipResult; message: string }>(
      `${environment.apiUrl}/api/relative/calculate-relationship`,
      {
        person1Id: this.selectedPerson1.id,
        person2Id: this.selectedPerson2.id
      }
    ).subscribe({
      next: (response) => {
        this.stopProgressTracking();
        this.isLoadingRelationship = false;
        if (response.success) {
          this.progressPercentage = 100;
          const calcTime = response.data.calculationTimeMs 
            ? `${(response.data.calculationTimeMs / 1000).toFixed(2)}s` 
            : `${this.elapsedTime}s`;
          this.progressMessage = `Relationship found! (calculated in ${calcTime})`;
          // Small delay to show 100% before displaying result
          setTimeout(() => {
            this.relationshipResult = response.data;
            this.progressPercentage = 0;
          }, 500);
        } else {
          this.errorMessage = response.message || 'Failed to calculate relationship';
        }
      },
      error: (error) => {
        this.stopProgressTracking();
        this.isLoadingRelationship = false;
        this.errorMessage = error.error?.message || 'Failed to calculate relationship';
        console.error('Error calculating relationship:', error);
      }
    });
  }

  private startProgressSimulation(): void {
    // Simulate progress based on typical calculation phases
    let phaseIndex = 0;
    const phases = [
      { name: 'Direct', message: 'Checking direct relationships...', targetPercent: 20 },
      { name: 'CommonAncestor', message: 'Finding common ancestor...', targetPercent: 50 },
      { name: 'InLaw', message: 'Checking in-law relationships...', targetPercent: 70 },
      { name: 'BFS', message: 'Searching family tree...', targetPercent: 95 }
    ];

    this.progressTimer = setInterval(() => {
      if (phaseIndex < phases.length) {
        const phase = phases[phaseIndex];
        this.progressPhase = phase.name;
        this.progressMessage = phase.message;
        
        // Gradually increase progress towards target
        if (this.progressPercentage < phase.targetPercent) {
          this.progressPercentage = Math.min(
            phase.targetPercent,
            this.progressPercentage + 2
          );
        } else {
          phaseIndex++;
        }
      } else {
        // In BFS phase, slowly increase to 95%
        if (this.progressPercentage < 95) {
          this.progressPercentage = Math.min(95, this.progressPercentage + 0.5);
        }
      }
    }, 200);
  }

  private updateProgressBasedOnTime(): void {
    // Adjust progress message based on elapsed time
    if (this.elapsedTime > 0 && this.elapsedTime < 1) {
      this.progressPhase = 'Direct';
      this.progressMessage = 'Checking direct relationships...';
    } else if (this.elapsedTime >= 1 && this.elapsedTime < 2) {
      this.progressPhase = 'CommonAncestor';
      this.progressMessage = 'Finding common ancestor...';
    } else if (this.elapsedTime >= 2 && this.elapsedTime < 3) {
      this.progressPhase = 'InLaw';
      this.progressMessage = 'Checking in-law relationships...';
    } else if (this.elapsedTime >= 3) {
      this.progressPhase = 'BFS';
      this.progressMessage = `Searching family tree... (${this.elapsedTime}s)`;
    }
  }

  private stopProgressTracking(): void {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
    if (this.elapsedTimer) {
      clearInterval(this.elapsedTimer);
      this.elapsedTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.stopProgressTracking();
  }

  getNextRelationshipLabel(path: string[], index: number): string {
    if (index < path.length - 1) {
      const nextItem = path[index + 1];
      if (nextItem === 'Child of' || nextItem === 'Parent of' || nextItem === 'Spouse') {
        return nextItem;
      }
    }
    // If current item is a relationship label, return it
    if (index < path.length) {
      const currentItem = path[index];
      if (currentItem === 'Child of' || currentItem === 'Parent of' || currentItem === 'Spouse') {
        return currentItem;
      }
    }
    return '';
  }

  getPathMethodLabel(method?: string): string {
    if (!method) return '';
    const labels: { [key: string]: string } = {
      'Direct': 'Direct Relationship',
      'CommonAncestor': 'Common Ancestor Path',
      'BFS': 'Breadth-First Search',
      'InLaw': 'In-Law Relationship',
      'Unknown': 'Unknown Path'
    };
    return labels[method] || method;
  }

  getRelationshipTypeIcon(type: string): string {
    const icons: { [key: string]: string } = {
      'Self': '👤',
      'Parent': '👨‍👩',
      'Child': '👶',
      'Sibling': '👫',
      'Spouse': '💑'
    };
    return icons[type] || '👤';
  }

  getRelationshipTypeColor(type: string): string {
    const colors: { [key: string]: string } = {
      'Self': '#667eea',
      'Parent': '#f093fb',
      'Child': '#4facfe',
      'Sibling': '#43e97b',
      'Spouse': '#fa709a'
    };
    return colors[type] || '#667eea';
  }

  getPerson1ImageUrl(): string | null {
    // First try the direct profile image URL
    if (this.relationshipResult?.person1ProfileImageUrl) {
      return this.relationshipResult.person1ProfileImageUrl;
    }
    // Fallback to detailed path
    if (this.relationshipResult?.detailedPath && this.relationshipResult.detailedPath.length > 0) {
      return this.relationshipResult.detailedPath[0]?.profileImageUrl || null;
    }
    return null;
  }

  getPerson2ImageUrl(): string | null {
    // First try the direct profile image URL
    if (this.relationshipResult?.person2ProfileImageUrl) {
      return this.relationshipResult.person2ProfileImageUrl;
    }
    // Fallback to detailed path
    if (this.relationshipResult?.detailedPath && this.relationshipResult.detailedPath.length > 0) {
      const lastSegment = this.relationshipResult.detailedPath[this.relationshipResult.detailedPath.length - 1];
      return lastSegment?.profileImageUrl || null;
    }
    return null;
  }

  getInitials(name: string): string {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  }

  goToHierarchy(relativeId: number): void {
    this.router.navigate(['/hierarchy', relativeId]);
  }

  getPhaseLabel(phase: string): string {
    const labels: { [key: string]: string } = {
      'Direct': 'Direct Relationship Check',
      'CommonAncestor': 'Common Ancestor Search',
      'InLaw': 'In-Law Relationship Check',
      'BFS': 'Breadth-First Search',
      '': 'Initializing...'
    };
    return labels[phase] || phase;
  }

  getPhaseIndex(phase: string): number {
    const phases = ['Direct', 'CommonAncestor', 'InLaw', 'BFS'];
    return phases.indexOf(phase);
  }

  // Expose Math to template
  Math = Math;
}

